import { useState, type FC, useCallback, type ReactElement, useEffect, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import { trackFollowButtonClick } from '../../utils/tracking/searchBrowse/trackFollowSearch.ts';
import { authLabels, locations } from '../../utils/tracking/shared/followSearchTracking.ts';
import { pageTypeConstants as PAGE_TYPES } from '../../constants/pageTypeConstants.ts';
import {
    checkRewardedSeller,
    checkTradeUserUnderTopLevelLoyaltyTiers,
} from 'dibs-buyer-layout/exports/sellerBrandingHelpers.ts';
import { sessionStorage } from 'dibs-browser-storage';
import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';

// Components
import { SaveSearchButton } from '../../components/global/SaveSearchButton/SaveSearchButton.tsx';
import { TooltipLoader } from '../../global/TooltipLoader.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { useSharedSaveSearchContext } from '../SharedSaveSearchContext/SharedSaveSearchContext.tsx';

import { type SharedFollowTopButton_itemSearch$key } from './__generated__/SharedFollowTopButton_itemSearch.graphql.ts';
import { type SharedFollowTopButton_user$key } from './__generated__/SharedFollowTopButton_user.graphql.ts';

import { useSbSelector } from '../../reducers/useSbSelector.ts';
import { isMultiVerticalPage } from '../helpers/multiVerticalPageHelpers.ts';
import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext.tsx';
import { useSaveSearchSuccessToast } from './useSaveSearchSuccessToast.tsx';
import { SAVE_SEARCH_FIRST_TIME_TOOLTIP_SHOWN_KEY } from './constants.ts';

import dibsCss from 'dibs-css';

const itemSearchFragment = graphql`
    fragment SharedFollowTopButton_itemSearch on ItemSearchQueryConnection {
        ...SaveSearchButton_itemSearch
        ...areFiltersApplied_itemSearch
        pageType
        searchTerm
        seller {
            ...sellerBrandingHelpers_seller
        }
    }
`;
const userFragment = graphql`
    fragment SharedFollowTopButton_user on User {
        ...sellerBrandingHelpers_user
    }
`;

type Props = {
    itemSearch: SharedFollowTopButton_itemSearch$key | null;
    user: SharedFollowTopButton_user$key | null | undefined;
    addLeadingSpace?: boolean;
};

const TOOLTIP_SHOW_DURATION = 3000;

export const SharedFollowTopButton: FC<Props> = ({
    itemSearch: itemSearchRef,
    user: userRef,
    addLeadingSpace,
}) => {
    const [loadHoverTooltip, setLoadHoverTooltip] = useState(false);
    const [loadSuccessTooltip, setLoadSuccessTooltip] = useState(false);
    const [showHoverTooltip, setShowHoverTooltip] = useState(false);
    const [showSuccessTooltip, setShowSuccessTooltip] = useState(false);
    const [showSaveSearchFirstTimeTooltip, setShowSaveSearchFirstTimeTooltip] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);

    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const user = useFragment(userFragment, userRef);

    const { pageType, searchTerm } = itemSearch || {};
    const isClient = useSbSelector(store => store.relayVariables.variables.isClient);
    const { isCategorySearch, isFollowing, followSearchValue, showFollowButton } =
        useSharedSaveSearchContext();
    const { isDriveSavedSearchAdoptionVariant } = useServerVarsContext();
    const { showSaveSearchSuccessToast, showRemoveSaveSearchToast } = useSaveSearchSuccessToast();

    const isSellerPage = pageType === PAGE_TYPES.MORE_FROM_SELLER || pageType === PAGE_TYPES.DEALER;
    const hideFollowButton =
        !showFollowButton || isMultiVerticalPage({ pageType, itemSearch, searchTerm });

    useVisibilityTracker({
        elementRef: buttonRef,
        isEnabled: isDriveSavedSearchAdoptionVariant && !hideFollowButton,
        onVisibilityChange: ({ isVisible: isElementVisible }) => {
            if (
                isElementVisible &&
                !sessionStorage.getItem(SAVE_SEARCH_FIRST_TIME_TOOLTIP_SHOWN_KEY)
            ) {
                setLoadHoverTooltip(true);
                setShowSaveSearchFirstTimeTooltip(true);
                sessionStorage.setItem(SAVE_SEARCH_FIRST_TIME_TOOLTIP_SHOWN_KEY, true);
            }
        },
    });

    useEffect(() => {
        if (showSaveSearchFirstTimeTooltip) {
            const tooltipTimeout = setTimeout(() => {
                setShowSaveSearchFirstTimeTooltip(false);
            }, TOOLTIP_SHOW_DURATION);

            return () => {
                clearTimeout(tooltipTimeout);
            };
        }

        return () => {};
    }, [showSaveSearchFirstTimeTooltip]);

    const handleFollowSearchCompleted = useCallback(
        (pageIsFollowed: boolean): void => {
            if (isDriveSavedSearchAdoptionVariant) {
                setShowHoverTooltip(false);
                if (pageIsFollowed) {
                    showSaveSearchSuccessToast({ isSeller: isSellerPage });
                } else {
                    showRemoveSaveSearchToast({ isSeller: isSellerPage });
                }
            } else if (pageIsFollowed) {
                // show success tooltip if user follows a search (but not if they unfollow)
                setLoadSuccessTooltip(true);
                setShowSuccessTooltip(true);
                setShowHoverTooltip(false);
                const timer = setTimeout(() => {
                    setShowSuccessTooltip(false);
                    clearTimeout(timer);
                }, 3000);
            }
            trackFollowButtonClick({
                isFollowing: pageIsFollowed,
                label: pageType === PAGE_TYPES.BUY ? 'top of buy page' : 'top of results',
                pageType,
            });
        },
        [
            isDriveSavedSearchAdoptionVariant,
            isSellerPage,
            pageType,
            showRemoveSaveSearchToast,
            showSaveSearchSuccessToast,
        ]
    );

    if (hideFollowButton) {
        return null;
    }

    // only show hover tooltip if user is not following search
    const mouseProps = {
        onMouseEnter: () => {
            setShowHoverTooltip(!isFollowing);
            setLoadHoverTooltip(!isFollowing);
        },
        onMouseLeave: () => {
            setShowHoverTooltip(false);
        },
    };

    const getSuccessToolTipContent = (): ReactElement => {
        if (
            isClient &&
            (pageType === PAGE_TYPES.MORE_FROM_SELLER || pageType === PAGE_TYPES.DEALER)
        ) {
            const shouldShowSellerName =
                checkRewardedSeller(itemSearch?.seller || null) ||
                checkTradeUserUnderTopLevelLoyaltyTiers(user || null);
            if (!shouldShowSellerName) {
                return (
                    <FormattedMessage
                        id="sb.SharedFollowTopButton.successTextHiddenSeller"
                        defaultMessage="Check your email for weekly updates."
                    />
                );
            }
        }

        return (
            <FormattedMessage
                id="sb.SharedFollowTopButton.successText"
                defaultMessage="Check your email for weekly updates in {name}."
                values={{
                    name: (
                        <span
                            data-tn="follow-search-value"
                            className={dibsCss.sassyFontBodyTypeHeavySizeNone}
                        >
                            {isCategorySearch ? followSearchValue : `"${followSearchValue}"`}
                        </span>
                    ),
                }}
            />
        );
    };

    return (
        <div
            {...mouseProps}
            ref={buttonRef}
            className={classnames(dibsCss.flexShrink0, dibsCss.relative, {
                [dibsCss.mlSmall]: addLeadingSpace,
            })}
            data-tn="top-save-search-button"
        >
            <SaveSearchButton
                itemSearch={itemSearch}
                locationLabel={authLabels[locations.SEARCH_BROWSE]}
                onComplete={handleFollowSearchCompleted}
                size="medium"
                type="secondary"
            />
            <TooltipLoader
                load={loadHoverTooltip}
                isVisible={
                    showHoverTooltip ||
                    (!!isDriveSavedSearchAdoptionVariant && showSaveSearchFirstTimeTooltip)
                }
                closeOnOutsideClick
                direction={!isDriveSavedSearchAdoptionVariant ? 'left' : 'right'}
                paddingSize={isDriveSavedSearchAdoptionVariant ? 'none' : 'default'}
            >
                {isDriveSavedSearchAdoptionVariant ? (
                    <div className={dibsCss.pSmaller}>
                        <FormattedMessage
                            id="sb.SharedFollowTopButton.tooltip"
                            defaultMessage="Get alerted when new items match your search"
                        />
                    </div>
                ) : (
                    <FormattedMessage
                        id="sb.SharedFollowTopButton.hoverText"
                        defaultMessage="Get weekly updates with new arrivals for this category."
                    />
                )}
            </TooltipLoader>
            {!isDriveSavedSearchAdoptionVariant && (
                <TooltipLoader
                    load={loadSuccessTooltip}
                    isVisible={showSuccessTooltip}
                    closeOnOutsideClick
                    direction={'left'}
                >
                    {getSuccessToolTipContent()}
                </TooltipLoader>
            )}
        </div>
    );
};
